import { ApolloCache } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'

import {
  ChildCommentsFragment,
  CommentEdge,
  CommentsPaginated,
  GetPostListDocument,
  RequestedCommentFragment,
  RequestedCommentFragmentDoc,
  useCommentAddedSubscription,
  useCommentCreateMutation,
  useCommentDeleteMutation,
  useCommentDeletedSubscription,
  useCommentUpdateMutation,
} from '@/generated/graphql'

const getCommentParentRef = (cache: ApolloCache<any>, comment: RequestedCommentFragment) => {
  const parent = comment.parentCommentId
    ? { __typename: 'Comment', commentId: comment.parentCommentId }
    : { __typename: 'Post', postId: comment.postId }
  return cache.identify(parent)
}

const getCommentPosts = (cache: ApolloCache<any>, comment: RequestedCommentFragment & ChildCommentsFragment) => {
  return Object.values(cache.extract()).filter(
    (c: any) => c.__typename === 'Post' && [c.postId, c.originalPostId].includes(comment.postId)
  )
}

export const addNewCommentToCache = (
  cache: ApolloCache<any>,
  comment: RequestedCommentFragment & ChildCommentsFragment
) => {
  getCommentPosts(cache, comment).forEach((p: any) => {
    cache.modify({
      id: getCommentParentRef(cache, {
        ...comment,
        postId: p.postId,
      }),
      fields: {
        comments(commentPaginated: CommentsPaginated, { readField }) {
          const newCommentRef = cache.writeFragment({
            data: comment,
            fragment: RequestedCommentFragmentDoc,
            fragmentName: 'RequestedComment',
          })
          if (commentPaginated.edges.find((ref: any) => readField('commentId', ref.node) === comment.commentId)) {
            return commentPaginated
          }
          return {
            ...commentPaginated,
            edges: commentPaginated.edges.concat({
              __typename: 'CommentEdge',
              cursor: '',
              node: newCommentRef as any,
            } as CommentEdge),
            totalCount: commentPaginated.totalCount + 1,
          }
        },
      },
    })
  })
}

const deleteCommentToCache = (cache: ApolloCache<any>, comment: RequestedCommentFragment & ChildCommentsFragment) => {
  getCommentPosts(cache, comment).forEach((p: any) => {
    cache.modify({
      id: getCommentParentRef(cache, {
        ...comment,
        postId: p.postId,
      }),
      fields: {
        comments(commentPaginated: CommentsPaginated, { readField }) {
          if (commentPaginated.edges.every((ref: any) => readField('commentId', ref.node) !== comment.commentId)) {
            return commentPaginated
          }

          return {
            ...commentPaginated,
            edges: commentPaginated.edges.filter(
              (ref: any) =>
                readField('commentId', ref.node) !== comment.commentId &&
                readField('parentCommentId', ref.node) !== comment.commentId
            ),
            totalCount: commentPaginated.totalCount - 1,
          }
        },
      },
    })
  })
}

export const useCreateComment = () => {
  return useCommentCreateMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      addNewCommentToCache(cache, data.commentCreate)
    },
  })
}

export const useUpdateComment = () => {
  return useCommentUpdateMutation()
}

export const useDeleteComment = (comment: RequestedCommentFragment) => {
  const [deleteComment] = useCommentDeleteMutation({
    variables: {
      comment: {
        commentId: comment.commentId,
      },
    },
    refetchQueries: [getOperationName(GetPostListDocument)!],
    update(cache) {
      cache.modify({
        id: getCommentParentRef(cache, comment),
        fields: {
          comments(commentPaginated: CommentsPaginated, { readField }) {
            return {
              ...commentPaginated,
              edges: commentPaginated.edges.filter(
                (ref: any) => readField('commentId', ref.node) !== comment.commentId
              ),
              totalCount: Math.max(commentPaginated.totalCount - 1, 0),
            }
          },
        },
      })
    },
  })
  return deleteComment
}

export const useCommentAdded = ({ postId }: { postId: number }) => {
  useCommentAddedSubscription({
    variables: {
      postId: postId,
    },
    onSubscriptionData({ client, subscriptionData }) {
      if (!subscriptionData.data) {
        return
      }
      const newComment = subscriptionData.data.commentAdded
      addNewCommentToCache(client.cache, newComment)
    },
  })
}

export const useCommentDeleted = ({ postId }: { postId: number }) => {
  useCommentDeletedSubscription({
    variables: {
      postId,
    },
    onSubscriptionData({ client, subscriptionData }) {
      if (!subscriptionData.data) {
        return
      }
      const comment = subscriptionData.data.commentDeleted
      deleteCommentToCache(client.cache, comment)
    },
  })
}
